import "./Listbox.css";

// eslint-disable-next-line no-restricted-imports
import {
  Listbox as HeadlessListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { type ComponentType, type ReactElement, type ReactNode, useId } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { SquareButton, type SquareButtonProps } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon } from "mds/icons";
import { ComponentEventPropertiesWithKey, trackEvent } from "utils/amplitude";

export type ListboxItemSize = "s" | "xs";

export type ListboxOption = {
  id: string;
  title: string;
  // Optional subtitle uses smaller text and is displayed inline with the title; also appears in closed trigger button
  subtitle?: ReactNode;
  // Optional icon to display at the start of the option; also appears in closed trigger button
  iconStart?: ReactNode;
  // Optional icon to display at the end of the option; does not appear in closed trigger button
  iconEnd?: ReactNode;
  className?: string;
};

export type ListboxDivider = { type: "divider" };

export type ListboxGroup<T extends ListboxOption = ListboxOption> = {
  type: "group";
  title: string;
  items: T[];
};

export type ListboxEntry<T extends ListboxOption = ListboxOption> =
  | T
  | ListboxDivider
  | ListboxGroup<T>;

type TriggerButtonProps = {
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
  isSelected?: boolean;
  eventName?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

export type ListboxProps<T extends ListboxOption> = ComponentEventPropertiesWithKey & {
  items: Array<ListboxEntry<T>>;
  selectedValue?: T["id"];
  className?: string;
  buttonKind?: SquareButtonProps["kind"];
  buttonSize?: SquareButtonProps["size"];
  // Text to display in the closed trigger button; if not provided, the title of the selected item is displayed
  buttonText?: ReactNode;
  // Custom open control (e.g. Chip or icon-only button). Replaces the default SquareButton trigger.
  triggerButton?: ReactElement<TriggerButtonProps>;
  // Anchor edge for the options panel. Defaults to bottom start.
  anchorTo?: "bottom start" | "bottom end";
  itemSize?: ListboxItemSize;
  onSelectionChange: (selectedItem: T) => void;
  disabled?: boolean;
  // No native read-only mode; treated as disabled when true (e.g. via FormField).
  readOnly?: boolean;
  openMenuEventName?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

const isDivider = (entry: ListboxEntry<ListboxOption>): entry is ListboxDivider =>
  "type" in entry && entry.type === "divider";

const isGroup = <T extends ListboxOption>(entry: ListboxEntry<T>): entry is ListboxGroup<T> =>
  "type" in entry && entry.type === "group";

const listboxEntryKey = (entry: ListboxEntry<ListboxOption> | undefined): string => {
  if (!entry) {
    return "edge";
  }
  if (isDivider(entry)) {
    return "divider";
  }
  if (isGroup(entry)) {
    return `group-${entry.title}`;
  }
  return entry.id;
};

const findSelectedOption = <T extends ListboxOption>(
  items: Array<ListboxEntry<T>>,
  selectedValue: T["id"] | undefined,
): T | null => {
  if (selectedValue === undefined) {
    return null;
  }
  for (const entry of items) {
    if (isGroup(entry)) {
      const found = entry.items.find((item) => item.id === selectedValue);
      if (found) {
        return found;
      }
    } else if (!isDivider(entry) && entry.id === selectedValue) {
      return entry;
    }
  }
  return null;
};

const LISTBOX_ITEM_SIZE_CLASSES: Record<ListboxItemSize, string> = {
  s: "py-2",
  xs: "py-[6px]",
};

const ListboxDividerRow = () => (
  <hr aria-hidden="true" className="my-2 border-0 border-t border-black-tint-93" />
);

const ListboxSubtitle = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={twMerge("mds-listbox-subtitle body-xs", className)}>{children}</span>
);

const ListboxOptionRow = <T extends ListboxOption>({
  item,
  itemSize,
}: {
  item: T;
  itemSize: ListboxItemSize;
}) => (
  <ListboxOption
    className={twMerge(
      "mds-listbox-option group flex cursor-pointer items-center gap-3 rounded-[8px] px-3 text-black-tint-20 data-[disabled]:cursor-not-allowed data-[focus]:bg-black-tint-93 data-[selected]:bg-blue-shade-20 data-[selected]:data-[focus]:bg-blue data-[disabled]:text-black-tint-55 data-[selected]:text-white supports-squircle:rounded-[16px] supports-squircle:corner-squircle",
      LISTBOX_ITEM_SIZE_CLASSES[itemSize],
      item.iconEnd && "justify-between",
      item.className,
    )}
    disabled={"disabled" in item ? (item.disabled as boolean) : false}
    value={item}
  >
    <div className="flex min-w-0 items-center gap-3">
      {item.iconStart}
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1">
        <span className="min-w-0 whitespace-normal break-words">{item.title}</span>
        {item.subtitle ? (
          <ListboxSubtitle className="whitespace-normal break-words">
            {item.subtitle}
          </ListboxSubtitle>
        ) : null}
      </div>
    </div>
    {item.iconEnd}
  </ListboxOption>
);

const ListboxGroupSection = <T extends ListboxOption>({
  group,
  itemSize,
}: {
  group: ListboxGroup<T>;
  itemSize: ListboxItemSize;
}) => {
  const headingId = useId();
  return (
    <div aria-labelledby={headingId} role="group">
      <div className="px-3 py-2 text-xs text-black-tint-55" id={headingId}>
        {group.title}
      </div>
      {group.items.map((item) => (
        <ListboxOptionRow item={item} itemSize={itemSize} key={item.id} />
      ))}
    </div>
  );
};

export const Listbox = <T extends ListboxOption>({
  items,
  selectedValue,
  className,
  buttonKind = "tertiary",
  buttonSize = "xs",
  buttonText,
  triggerButton,
  anchorTo = "bottom start",
  itemSize = "s",
  onSelectionChange,
  disabled,
  readOnly,
  openMenuEventName,
  eventName,
  eventProperties,
  eventPropertyKey,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}: ListboxProps<T>) => {
  const selectedItem = findSelectedOption(items, selectedValue);
  const isDisabled = disabled || readOnly;

  const handleChange = (item: T) => {
    if (eventName) {
      trackEvent(eventName, {
        eventCategory: "Button press",
        ...eventProperties,
        [eventPropertyKey || "itemId"]: item.id,
      });
    }
    onSelectionChange(item);
  };

  return (
    <HeadlessListbox by="id" disabled={isDisabled} value={selectedItem} onChange={handleChange}>
      {({ open }) => (
        <>
          {triggerButton ? (
            <ListboxButton
              // Spread trigger props first so `as={triggerButton.type}` is not
              // overwritten by an `as` on the trigger element (e.g. Chip as="button").
              {...triggerButton.props}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-required={ariaRequired}
              as={triggerButton.type as ComponentType<TriggerButtonProps>}
              className={twMerge(triggerButton.props.className, className)}
              disabled={isDisabled || triggerButton.props.disabled}
              eventName={openMenuEventName}
              id={id}
              isSelected={open}
            />
          ) : (
            <ListboxButton
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-required={ariaRequired}
              as={SquareButton}
              className={twMerge("min-w-0 justify-between gap-2", className)}
              disabled={isDisabled}
              eventName={openMenuEventName}
              id={id}
              isSelected={open}
              kind={buttonKind}
              size={buttonSize}
            >
              <span className="flex min-w-0 items-center gap-2">
                {!buttonText && selectedItem?.iconStart}
                {buttonText ? (
                  <span className="min-w-0 truncate">{buttonText}</span>
                ) : (
                  <span className="flex min-w-0 items-center gap-1 overflow-hidden">
                    <span className="min-w-0 shrink truncate">
                      {selectedItem?.title || t("common.select_an_item")}
                    </span>
                    {selectedItem?.subtitle ? (
                      <ListboxSubtitle className="min-w-0 shrink-[999] truncate">
                        {selectedItem.subtitle}
                      </ListboxSubtitle>
                    ) : null}
                  </span>
                )}
              </span>
              <ChevronDownIcon className="shrink-0" />
            </ListboxButton>
          )}
          <ListboxOptions
            anchor={{ to: anchorTo, padding: 12, gap: 4 }}
            // !fixed prevents additional scrollbar from appearing on the page when a listbox with a scrollbar is opened
            className="!fixed z-[110] min-w-[max(200px,var(--button-width))] !max-w-[520px] !overflow-hidden rounded-[12px] border border-black-tint-93 bg-white text-sm shadow-modal [--anchor-max-height:480px] supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
            portal
          >
            <div className="max-h-[inherit] overflow-auto p-2">
              {items.map((entry, index) => {
                if (isDivider(entry)) {
                  return (
                    <ListboxDividerRow
                      key={`divider-${listboxEntryKey(items[index - 1])}-${listboxEntryKey(items[index + 1])}`}
                    />
                  );
                }
                if (isGroup(entry)) {
                  const previousEntry = items[index - 1];
                  const showGroupDivider = index > 0 && !isDivider(previousEntry);
                  return (
                    <div key={`group-${entry.title}`}>
                      {showGroupDivider && <ListboxDividerRow />}
                      <ListboxGroupSection group={entry} itemSize={itemSize} />
                    </div>
                  );
                }
                return <ListboxOptionRow item={entry} itemSize={itemSize} key={entry.id} />;
              })}
            </div>
          </ListboxOptions>
        </>
      )}
    </HeadlessListbox>
  );
};
